// Coverage of the pending pedido queue by current stock and by the turno being
// planned. Pure functions over plain data so the allocation can be reasoned
// about (and tested) without React.
//
// Deliberately NOT driven by any stored plan→pedido link: 79% of products are
// wanted by 2+ live pedidos at once, so a planned product's output serves the
// priority queue rather than one pedido. Everything here is derived.

export type CoverageState =
    // delivered in full already — demands nothing, consumes nothing
    | 'complete'
    // stock on hand covers it (matches Pedidos por surtir's green)
    | 'stock'
    // stock fell short but THIS turno closes the gap — the new signal
    | 'plan'
    // still short after both
    | 'partial'
    // nothing allocated
    | 'none'
    // Outstanding in its own unit, but with no kilos to allocate against. See
    // `measurable` below: allocation runs in kilos, so such a line cannot be
    // covered, planned, or turned into hours — and must not be painted as
    // covered merely because zero kilos went zero kilos' worth of the way.
    | 'unmeasured';

export interface CoverageLine {
    order_request_id: number;
    product_id: number | null;
    product_description: string;
    // The unit the pedido line was written in — bultos when it carries groups,
    // else kilos. Both quantities are shown; this decides which leads.
    unit: 'bultos' | 'kg';
    remaining_kilos: number;
    remaining_groups: number;
    from_inventory_kilos: number;
    from_plan_kilos: number;
    uncovered_kilos: number;
    // Same three, expressed in the line's own bultos ratio (see toGroups).
    from_inventory_groups: number;
    from_plan_groups: number;
    uncovered_groups: number;
    // Full product inventory and its committed warning, independent from the
    // portion allocated to this particular pedido line.
    inventory_kilos: number;
    inventory_groups: number;
    inventory_kilos_committed: number;
    inventory_groups_committed: number;
    // Captured production for this product/date/turno/sucursal. It is shown
    // separately and never reduces the plan forecast.
    real_kilos: number;
    real_groups: number;
    state: CoverageState;
}

export interface CoveragePedido {
    order_request_id: number;
    order_code: number | null;
    account_name: string;
    order_request_status_id: number;
    order_request_status_name: string;
    order_request_date: string | null;
    order_request_estimated_delivery_date: string | null;
    priority: number;
    lines: CoverageLine[];
}

// The subset of getOptimizedRequestProducts this module needs. Declared
// structurally so the generated query type satisfies it without a cast.
export interface DemandRow {
    order_request_id?: number | null;
    order_code?: number | null;
    account_name: string;
    order_request_status_id?: number | null;
    order_request_status_name: string;
    order_request_date?: string | null;
    order_request_estimated_delivery_date?: string | null;
    priority?: number | null;
    product_id?: number | null;
    product_description?: string | null;
    order_request_kilos?: number | null;
    order_request_groups?: number | null;
    order_sale_remaining_kilos?: number | null;
    order_sale_remaining_groups?: number | null;
}

export interface StockEntry {
    kilos: number;
    groups: number;
    kilos_committed?: number;
    groups_committed?: number;
}

// Allocation runs in KILOS throughout, because that is the unit the kg/hr rate
// speaks and the unit the drawer converts into planned hours. Bultos are then
// derived from each line's OWN kilos:groups ratio, so a line's two numbers stay
// internally consistent.
//
// Known, intentional difference from Pedidos por surtir: that board allocates in
// each line's native unit, so a bultos-native line can land on a marginally
// different covered figure there. The basis differs because this page has to
// turn coverage into hours; the ordering and the pool semantics are identical.
const toGroups = (
    kilos: number,
    remainingKilos: number,
    remainingGroups: number,
): number => {
    if (remainingKilos <= 0 || remainingGroups <= 0) return 0;
    return (kilos / remainingKilos) * remainingGroups;
};

export function computeCoverage({
    demandRows,
    inventory,
    plannedKilosByProduct,
    actualByProduct,
}: {
    demandRows: DemandRow[];
    inventory: Map<number, StockEntry>;
    // Kilos this plan would produce per product, from the LIVE edited rows.
    plannedKilosByProduct: Map<number, number>;
    // Captured production is informational only; it does not reduce the plan
    // forecast or the allocation pool.
    actualByProduct?: Map<number, { kilos: number; groups: number }>;
}): CoveragePedido[] {
    const rowsByOrder = new Map<number, DemandRow[]>();
    demandRows.forEach((row) => {
        if (!row.order_request_id) return;
        const rows = rowsByOrder.get(row.order_request_id) || [];
        rows.push(row);
        rowsByOrder.set(row.order_request_id, rows);
    });

    // Identical ordering to Pedidos por surtir: "En producción" (status 2) above
    // "Pendiente" (1); within a status, manual priority, then oldest first
    // (kitchen FIFO), then order code. Reproducing it is what makes the two
    // pages agree about who gets the stock.
    const sortedOrders = Array.from(rowsByOrder.values()).sort((a, b) => {
        const statusA = a[0].order_request_status_id || 0;
        const statusB = b[0].order_request_status_id || 0;
        if (statusA !== statusB) return statusB - statusA;
        const priorityA = a[0].priority || 0;
        const priorityB = b[0].priority || 0;
        if (priorityA !== priorityB) return priorityA - priorityB;
        const dateA = a[0].order_request_date
            ? new Date(a[0].order_request_date).getTime()
            : Number.MAX_SAFE_INTEGER;
        const dateB = b[0].order_request_date
            ? new Date(b[0].order_request_date).getTime()
            : Number.MAX_SAFE_INTEGER;
        if (dateA !== dateB) return dateA - dateB;
        return (a[0].order_code || 0) - (b[0].order_code || 0);
    });

    // Two pools, walked in the same order. Two passes rather than one merged
    // pool because the question is "what does THIS turno change?" — a single
    // pool can only answer "covered".
    const stockPool = new Map<number, number>();
    inventory.forEach((entry, productId) => {
        stockPool.set(productId, Math.max(0, entry.kilos || 0));
    });
    const planPool = new Map<number, number>();
    plannedKilosByProduct.forEach((kilos, productId) => {
        planPool.set(productId, Math.max(0, kilos));
    });

    const take = (
        pool: Map<number, number>,
        productId: number | null | undefined,
        wanted: number,
    ): number => {
        if (!productId || wanted <= 0) return 0;
        const available = pool.get(productId) || 0;
        const taken = Math.min(wanted, available);
        if (taken > 0) pool.set(productId, available - taken);
        return taken;
    };

    return sortedOrders.map((rows) => {
        const first = rows[0];
        const lines: CoverageLine[] = rows.map((row) => {
            const useGroups = (row.order_request_groups || 0) > 0;
            // Clamp so over-delivery reads 0 rather than a negative number,
            // same as the board.
            const remainingKilos = Math.max(
                0,
                row.order_sale_remaining_kilos || 0,
            );
            const remainingGroups = Math.max(
                0,
                row.order_sale_remaining_groups || 0,
            );
            // "Delivered" is judged in the line's OWN unit — that is the number
            // the pedido was written in and the one the board reports against.
            const isComplete = useGroups
                ? remainingGroups === 0
                : remainingKilos === 0;

            // ...but allocation runs in kilos. A bultos line can therefore still
            // owe bultos while carrying no kilos to allocate (a product with no
            // captured group weight). Such a line takes nothing from either pool,
            // and without this flag it would land on `uncovered === 0` and be
            // painted "cubierto con inventario" — a green chip for a line nothing
            // was ever allocated to, with no add button to fix it.
            const measurable = remainingKilos > 0;

            // A completed line demands nothing, so it consumes from neither pool.
            const allocatable = !isComplete && measurable;
            const fromInventory = allocatable
                ? take(stockPool, row.product_id, remainingKilos)
                : 0;
            const fromPlan = allocatable
                ? take(
                      planPool,
                      row.product_id,
                      remainingKilos - fromInventory,
                  )
                : 0;
            const uncovered = Math.max(
                0,
                remainingKilos - fromInventory - fromPlan,
            );
            const stock = row.product_id
                ? inventory.get(row.product_id)
                : undefined;
            const real = row.product_id
                ? actualByProduct?.get(row.product_id)
                : undefined;

            const state: CoverageState = isComplete
                ? 'complete'
                : !measurable
                ? 'unmeasured'
                : uncovered <= 0 && fromPlan <= 0
                ? 'stock'
                : uncovered <= 0
                ? 'plan'
                : fromInventory > 0 || fromPlan > 0
                ? 'partial'
                : 'none';

            return {
                order_request_id: row.order_request_id as number,
                product_id: row.product_id || null,
                product_description: row.product_description || '',
                unit: useGroups ? 'bultos' : 'kg',
                remaining_kilos: remainingKilos,
                remaining_groups: remainingGroups,
                from_inventory_kilos: fromInventory,
                from_plan_kilos: fromPlan,
                uncovered_kilos: uncovered,
                from_inventory_groups: toGroups(
                    fromInventory,
                    remainingKilos,
                    remainingGroups,
                ),
                from_plan_groups: toGroups(
                    fromPlan,
                    remainingKilos,
                    remainingGroups,
                ),
                uncovered_groups: toGroups(
                    uncovered,
                    remainingKilos,
                    remainingGroups,
                ),
                inventory_kilos: stock?.kilos || 0,
                inventory_groups: stock?.groups || 0,
                inventory_kilos_committed: stock?.kilos_committed || 0,
                inventory_groups_committed: stock?.groups_committed || 0,
                real_kilos: real?.kilos || 0,
                real_groups: real?.groups || 0,
                state,
            };
        });

        return {
            order_request_id: first.order_request_id as number,
            order_code: first.order_code || null,
            account_name: first.account_name,
            order_request_status_id: first.order_request_status_id || 0,
            order_request_status_name: first.order_request_status_name,
            order_request_date: first.order_request_date || null,
            order_request_estimated_delivery_date:
                first.order_request_estimated_delivery_date || null,
            priority: first.priority || 0,
            lines,
        };
    });
}

// Kilos still requiring production for a product across the queue. Demand
// already covered by positive stock needs no production, but a negative stock
// balance is a debt that production must repair. Planned output beyond the
// queue's demand is already serving that debt, so repeated clicks do not keep
// adding the same deficit.
export function productionNeededKilosByProduct(
    pedidos: CoveragePedido[],
    inventory: Map<number, StockEntry> = new Map(),
    plannedKilosByProduct: Map<number, number> = new Map(),
): Map<number, number> {
    const unmetByProduct = new Map<number, number>();
    const allocatedPlanByProduct = new Map<number, number>();
    const activeProducts = new Set<number>();
    pedidos.forEach((pedido) => {
        pedido.lines.forEach((line) => {
            if (!line.product_id) return;
            if (line.uncovered_kilos > 0 || line.from_plan_kilos > 0) {
                activeProducts.add(line.product_id);
            }
            if (line.uncovered_kilos > 0) {
                unmetByProduct.set(
                    line.product_id,
                    (unmetByProduct.get(line.product_id) || 0) +
                        line.uncovered_kilos,
                );
            }
            if (line.from_plan_kilos > 0) {
                allocatedPlanByProduct.set(
                    line.product_id,
                    (allocatedPlanByProduct.get(line.product_id) || 0) +
                        line.from_plan_kilos,
                );
            }
        });
    });

    const totals = new Map<number, number>();
    activeProducts.forEach((productId) => {
        const unmet = unmetByProduct.get(productId) || 0;
        const stockDebt = Math.max(
            0,
            -(inventory.get(productId)?.kilos || 0),
        );
        const planned = Math.max(
            0,
            plannedKilosByProduct.get(productId) || 0,
        );
        const plannedSurplus = Math.max(
            0,
            planned - (allocatedPlanByProduct.get(productId) || 0),
        );
        const remainingStockDebt = Math.max(0, stockDebt - plannedSurplus);
        const needed = unmet + remainingStockDebt;
        if (needed > 0) {
            totals.set(productId, needed);
        }
    });

    return totals;
}

// Kept as a small compatibility wrapper for callers that only need the old
// positive-stock/uncovered behavior.
export function uncoveredKilosByProduct(
    pedidos: CoveragePedido[],
): Map<number, number> {
    const totals = new Map<number, number>();
    pedidos.forEach((pedido) => {
        pedido.lines.forEach((line) => {
            if (!line.product_id || line.uncovered_kilos <= 0) return;
            totals.set(
                line.product_id,
                (totals.get(line.product_id) || 0) + line.uncovered_kilos,
            );
        });
    });
    return totals;
}
